import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useMutation } from "react-query";

import {
  GetUserDocumentationPostsRequest,
  getUserDocumentationPosts
} from "api/userDocumentation";

import styles from "components/user-documentation/styles/documentationModal.module.scss";

import PostPreview from "../PostPreview";
import { appendToPostList, setPostList, updateTotalPosts, usePost } from "../context";
import { NotificationItem } from "../types";

const page_size = 6;

interface NotificationSidebarProps {
  search: string;
  tags: string[];
  setLoadPostsError: (error: string) => void;
  onClick: (postId: string) => void;
  onNotificationsUpdate?: (notifications: NotificationItem[]) => void;
  selectedItemId?: string;
}

const NotificationSidebar = ({
  search,
  tags,
  setLoadPostsError,
  onClick,
  onNotificationsUpdate,
  selectedItemId
}: NotificationSidebarProps) => {
  const [{ posts, totalPosts }, postDispatch] = usePost();

  // state
  const [allPostsLoaded, setAllPostsLoaded] = useState(false);
  const [pageNum, setPageNum] = useState(0);

  const options = {};
  const [ref, inView] = useInView(options);

  const getPostsMutation = useMutation(
    (request: GetUserDocumentationPostsRequest) => getUserDocumentationPosts(request),
    {
      onError: (error) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err = error as any;
        if (err && err.response?.data) {
          // eslint-disable-next-line no-console
          console.error("Get User Doc error", err.response?.data);
        }

        setPostList(postDispatch, []);
        updateTotalPosts(postDispatch, 0);
        setLoadPostsError("Failed to load notifications");
      }
    }
  );

  useEffect(() => {
    if (!getPostsMutation.data?.data) return;
    setLoadPostsError("");

    const loadedPosts = getPostsMutation.data?.data?.posts ?? [];

    if (getPostsMutation.variables.pageIndex > 1) {
      appendToPostList(postDispatch, loadedPosts);
    } else {
      setPostList(postDispatch, loadedPosts);
    }

    // Set the current page number to the loaded index.
    setPageNum(getPostsMutation.variables.pageIndex);

    if (getPostsMutation.data?.data) {
      updateTotalPosts(postDispatch, getPostsMutation.data?.data?.count);
    }
  }, [getPostsMutation.data, postDispatch]);

  const retrievePosts = useCallback(
    (append = false) => {
      // Encode both values: a search term containing "&", "#" or "+" would
      // otherwise be read as part of the URL rather than as text to look for.
      const params = new URLSearchParams();
      if (tags.length) {
        params.append("tag", tags.join(","));
      }
      if (search !== "") {
        params.append("search", search);
      }
      const query = params.toString() ? `?${params.toString()}` : "";

      // If appending we want the next page, otherwise take the first.
      const pageIndex = append ? pageNum + 1 : 1;

      getPostsMutation.mutate({
        pageIndex: pageIndex,
        pageSize: page_size,
        query: query
      });
    },
    [tags, search, getPostsMutation]
  );

  // Fetch again whenever the search text or the tag selection changes. The
  // search text arrives already debounced, so this is one request per pause in
  // typing rather than one per keystroke.
  useEffect(() => {
    retrievePosts();
    // retrievePosts is deliberately omitted: it changes identity on every
    // render, which would turn this into an endless fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, search]);

  useEffect(() => {
    setAllPostsLoaded(posts?.length >= totalPosts);
  }, [posts, totalPosts]);

  useEffect(() => {
    if (inView && !allPostsLoaded) {
      retrievePosts(true);
    }
  }, [inView]);

  // Update parent component's notifications state when posts change
  useEffect(() => {
    if (onNotificationsUpdate && posts.length > 0) {
      onNotificationsUpdate(posts as NotificationItem[]);
    }
  }, [posts, onNotificationsUpdate]);

  // Tell the two empty cases apart: a search that matched nothing is a
  // different message from having no notifications at all.
  const isFiltered = search !== "" || tags.length > 0;
  // isLoading is false before the first request is even sent, so testing it
  // alone showed "No notifications yet" for a moment on opening, before any
  // answer had come back.
  const hasFetched = getPostsMutation.isSuccess || getPostsMutation.isError;
  if (posts.length === 0 && hasFetched && !getPostsMutation.isLoading) {
    return (
      <div className={styles.emptyState} data-testid="notifications-empty-state">
        {isFiltered
          ? `No notifications match “${search || tags.join(", ")}”`
          : "No notifications yet"}
      </div>
    );
  }

  return (
    <>
      {posts.map((post, index) => {
        const lastElement = index === posts.length - 1;
        return (
          <div
            key={index}
            className={styles.notificationSidebarItem}
            ref={lastElement ? ref : null}
            onClick={() => onClick(post.id.toString())}>
            <PostPreview
              title={post.title}
              date={post.date}
              content={post.content}
              id={post.id}
              isModalView={true}
              onPostClick={() => onClick(post.id.toString())}
              isActive={selectedItemId === post.id.toString()}
            />
          </div>
        );
      })}
    </>
  );
};

export default NotificationSidebar;
